import PropTypes from 'prop-types';
import React from 'react';

import {applyScratchAiPlan} from '../../lib/maipai-ai/scratch-ai-inserter';
import styles from './maipai-ai-programming.css';

const STORAGE_KEY = 'maipai:scratch-ai:conversation:v1';
const MAX_MESSAGES = 10;
const PANEL_MARGIN = 8;
const REVISION_KEYWORDS = [
    '上一版',
    '上版',
    '刚才',
    '这个方案',
    '这个作品',
    '继续',
    '改成',
    '修改',
    '优化',
    '加上',
    '增加',
    '慢一点',
    '快一点',
    '一直循环',
    'revise',
    'modify',
    'continue'
];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const normalizePromptText = value => `${value || ''}`.toLowerCase().replace(/\s+/g, '');

const isRevisionPrompt = value => {
    const normalized = normalizePromptText(value);
    return REVISION_KEYWORDS.some(keyword => normalized.includes(normalizePromptText(keyword)));
};

const clampPanelPosition = position => {
    if (typeof window === 'undefined') return position;
    const panelWidth = Math.min(980, window.innerWidth - 24);
    const panelHeight = Math.min(720, window.innerHeight - 88);
    return {
        x: clamp(position.x, PANEL_MARGIN, Math.max(PANEL_MARGIN, window.innerWidth - panelWidth - PANEL_MARGIN)),
        y: clamp(position.y, PANEL_MARGIN, Math.max(PANEL_MARGIN, window.innerHeight - panelHeight - PANEL_MARGIN))
    };
};

const getDefaultPanelPosition = () => {
    if (typeof window === 'undefined') return {x: 245, y: 70};
    const panelWidth = Math.min(980, window.innerWidth - 24);
    return clampPanelPosition({
        x: window.innerWidth - panelWidth - 18,
        y: 64
    });
};

const getCurrentSprites = vm => vm.runtime.targets
    .filter(target => target && !target.isStage)
    .map(target => ({name: target.getName()}));

const createEmptySession = () => ({
    messages: [],
    lastPlan: null
});

const normalizeStoredSession = value => {
    if (!value || typeof value !== 'object') return createEmptySession();
    return {
        messages: Array.isArray(value.messages) ? value.messages.slice(-MAX_MESSAGES) : [],
        lastPlan: value.lastPlan || null
    };
};

const loadSession = () => {
    if (typeof window === 'undefined' || !window.localStorage) return createEmptySession();
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        return raw ? normalizeStoredSession(JSON.parse(raw)) : createEmptySession();
    } catch (error) {
        return createEmptySession();
    }
};

const saveSession = session => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizeStoredSession(session)));
    } catch (error) {
        // Local cache is only a convenience. Scratch editing should continue if storage is unavailable.
    }
};

const buildAssistantContent = plan => {
    if (!plan) return '已生成新的积木方案';
    return `${plan.planTitle || '新方案'}：${plan.summary || '可以继续修改或确认插入。'}`;
};

const compactHistoryForRequest = messages => messages.slice(-MAX_MESSAGES).map(message => ({
    role: message.role,
    content: message.content,
    plan: message.plan,
    createdAt: message.createdAt
}));

const getClarificationQuestions = plan => {
    if (!plan || !plan.taskBreakdown) return [];
    const questions = plan.taskBreakdown.clarificationQuestions;
    return Array.isArray(questions) ? questions.filter(Boolean).slice(0, 3) : [];
};

const getGenerationProcess = plan => {
    if (!plan || !Array.isArray(plan.generationProcess)) return [];
    return plan.generationProcess
        .filter(step => step && (step.title || step.detail))
        .map(step => ({
            title: step.title || '公开说明',
            detail: step.detail || ''
        }))
        .slice(0, 8);
};

const getImplementationNotes = plan => {
    if (!plan || !Array.isArray(plan.implementationNotes)) return [];
    return plan.implementationNotes.filter(Boolean).slice(0, 10);
};

const getRecommendedReplies = question => {
    const normalized = `${question || ''}`.toLowerCase();
    if (
        normalized.includes('game') ||
        normalized.includes('游戏') ||
        normalized.includes('坦克') ||
        normalized.includes('射击') ||
        normalized.includes('跑酷') ||
        normalized.includes('迷宫')
    ) {
        return [
            {
                label: '坦克大战游戏',
                detail: '玩家坦克用方向键移动，按空格发射炮弹，敌方坦克巡逻，命中后加分。'
            },
            {
                label: '射击游戏',
                detail: '玩家左右移动，按空格发射子弹，敌人从上方出现，命中后加分。'
            },
            {
                label: '跑酷游戏',
                detail: '角色自动前进，按空格跳过障碍，碰到障碍就重新开始。'
            },
            {
                label: '迷宫游戏',
                detail: '用方向键移动角色，避开墙壁，找到终点后说出胜利提示。'
            }
        ];
    }

    return [
        {
            label: '简单版',
            detail: '先做一个能运行的基础版本，后面再继续加细节。'
        },
        {
            label: '完整一点',
            detail: '加入目标、得分或失败条件，让作品更像一个完整项目。'
        },
        {
            label: '自由补充',
            detail: '我在下面输入自己的要求。'
        }
    ];
};

const ActionSummary = ({action}) => {
    if (action.type === 'repeat') {
        return <li>重复 {action.times} 次：{(action.actions || []).map(child => child.type).join('、')}</li>;
    }
    if (action.type === 'forever') {
        return <li>重复执行：{(action.actions || []).map(child => child.type).join('、')}</li>;
    }
    if (action.type === 'goToTarget') {
        return <li>goToTarget: {action.target === 'random-position' ? 'random-position' : 'mouse-pointer'}</li>;
    }
    if (action.type === 'glideToTarget') {
        return <li>glideToTarget: {action.seconds || 0.2}s to {action.target === 'random-position' ? 'random-position' : 'mouse-pointer'}</li>;
    }
    if (action.type === 'setPenColor') {
        return <li>setPenColor: {action.color || '#ff6680'}</li>;
    }
    if (action.type === 'changePenColor') {
        return <li>changePenColor: {action.amount || 10}</li>;
    }
    if (action.type === 'setPenSize') {
        return <li>setPenSize: {action.size || 3}</li>;
    }
    if (action.type === 'changePenSize') {
        return <li>changePenSize: {action.amount || 1}</li>;
    }
    if (action.type === 'changeX') {
        return <li>changeX: {action.dx || 10}</li>;
    }
    const text = action.text || action.message || action.steps || action.degrees || action.soundName || '';
    return <li>{action.type}{text ? `：${text}` : ''}</li>;
};

ActionSummary.propTypes = {
    action: PropTypes.shape({
        type: PropTypes.string.isRequired
    }).isRequired
};

const getActionLabel = action => {
    if (!action || !action.type) return 'unknown';
    if (action.type === 'repeat') return `repeat ${action.times || 10}`;
    if (action.type === 'forever') return 'forever';
    if (action.type === 'ifTouchingSprite') return `ifTouchingSprite ${action.spriteName || ''}`;
    if (action.type === 'goToTarget') return `goToTarget ${action.target || 'mouse-pointer'}`;
    if (action.type === 'glideToTarget') return `glideToTarget ${action.seconds || 0.2}s ${action.target || 'mouse-pointer'}`;
    if (action.type === 'goTo') return `goTo x:${action.x || 0} y:${action.y || 0}`;
    if (action.type === 'goToSprite') return `goToSprite ${action.spriteName || ''}`;
    if (action.type === 'changeX') return `changeX ${action.dx || 10}`;
    if (action.type === 'changeY') return `changeY ${action.dy || 10}`;
    if (action.type === 'moveSteps') return `moveSteps ${action.steps || 10}`;
    if (action.type === 'say') return `say ${action.text || ''}`;
    if (action.type === 'wait') return `wait ${action.seconds || 1}s`;
    if (action.type === 'broadcast') return `broadcast ${action.message || ''}`;
    if (action.type === 'setVariable') return `setVariable ${action.variableName || ''} = ${action.value || 0}`;
    if (action.type === 'changeVariable') return `changeVariable ${action.variableName || ''} by ${action.value || 1}`;
    if (action.type === 'playSound') return `playSound ${action.soundName || 'pop'}`;
    return action.type;
};

const FullActionList = ({actions}) => {
    if (!Array.isArray(actions) || !actions.length) return null;
    return (
        <ul className={styles.actionList}>
            {actions.map((action, index) => (
                <FullActionSummary
                    action={action}
                    key={`${index}-${action.type}`}
                />
            ))}
        </ul>
    );
};

const FullActionSummary = ({action}) => (
    <li>
        <span>{getActionLabel(action)}</span>
        {action && Array.isArray(action.actions) ? <FullActionList actions={action.actions} /> : null}
    </li>
);

FullActionList.propTypes = {
    actions: PropTypes.arrayOf(PropTypes.shape({
        type: PropTypes.string.isRequired
    }))
};

FullActionList.defaultProps = {
    actions: []
};

FullActionSummary.propTypes = {
    action: PropTypes.shape({
        type: PropTypes.string.isRequired,
        actions: PropTypes.array
    }).isRequired
};

const getEventLabel = event => {
    if (!event || !event.type) return 'greenFlag';
    if (event.type === 'keyPressed') return `keyPressed ${event.key || 'space'}`;
    if (event.type === 'spriteClicked') return 'spriteClicked';
    if (event.type === 'broadcastReceived') return `broadcastReceived ${event.message || ''}`;
    return event.type;
};

const ProcessTranscript = ({loading, plan}) => {
    const process = getGenerationProcess(plan);
    const notes = getImplementationNotes(plan);

    if (loading) {
        return (
            <div className={styles.processWaiting}>
                <strong>等待模型返回公开生成内容</strong>
                <p>这里不会再播放固定步骤，生成完成后会直接显示 AI 返回的公开分析、实现选择和校验说明。</p>
            </div>
        );
    }

    if (!process.length && !notes.length) {
        return (
            <div className={styles.processWaiting}>
                <strong>还没有生成记录</strong>
                <p>描述作品目标后，这里会显示 AI 返回的公开生成内容。</p>
            </div>
        );
    }

    return (
        <div className={styles.processTranscript}>
            {process.map((step, index) => (
                <article className={styles.processEntry} key={`${step.title}-${index}`}>
                    <span>{index + 1}</span>
                    <div>
                        <strong>{step.title}</strong>
                        <p>{step.detail}</p>
                    </div>
                </article>
            ))}
            {notes.length ? (
                <div className={styles.implementationNotes}>
                    <strong>实现说明</strong>
                    <ul>
                        {notes.map((note, index) => (
                            <li key={`${note}-${index}`}>{note}</li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </div>
    );
};

ProcessTranscript.propTypes = {
    loading: PropTypes.bool.isRequired,
    plan: PropTypes.shape({
        generationProcess: PropTypes.arrayOf(PropTypes.shape({
            title: PropTypes.string,
            detail: PropTypes.string
        })),
        implementationNotes: PropTypes.arrayOf(PropTypes.string)
    })
};

ProcessTranscript.defaultProps = {
    plan: null
};

const MaipaiAiProgramming = ({vm}) => {
    const [open, setOpen] = React.useState(false);
    const [prompt, setPrompt] = React.useState('');
    const [session, setSession] = React.useState(loadSession);
    const [plan, setPlan] = React.useState(() => loadSession().lastPlan);
    const [loading, setLoading] = React.useState(false);
    const [applying, setApplying] = React.useState(false);
    const [error, setError] = React.useState('');
    const [panelPosition, setPanelPosition] = React.useState(getDefaultPanelPosition);
    const panelRef = React.useRef(null);
    const dragStateRef = React.useRef(null);

    React.useEffect(() => {
        saveSession(session);
    }, [session]);

    React.useEffect(() => {
        if (!open) return undefined;
        const handleResize = () => {
            setPanelPosition(previous => clampPanelPosition(previous));
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [open]);

    const handleHeaderPointerDown = event => {
        if (event.button !== 0) return;
        if (event.target.closest('button, textarea, input, a')) return;
        const rect = panelRef.current ? panelRef.current.getBoundingClientRect() : null;
        dragStateRef.current = {
            offsetX: event.clientX - (rect ? rect.left : panelPosition.x),
            offsetY: event.clientY - (rect ? rect.top : panelPosition.y)
        };
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const handleHeaderPointerMove = event => {
        if (!dragStateRef.current) return;
        const {offsetX, offsetY} = dragStateRef.current;
        setPanelPosition(clampPanelPosition({
            x: event.clientX - offsetX,
            y: event.clientY - offsetY
        }));
    };

    const handleHeaderPointerUp = event => {
        dragStateRef.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    };

    const appendConversation = (userPrompt, nextPlan) => {
        const now = Date.now();
        const userMessage = {
            role: 'user',
            content: userPrompt,
            createdAt: now
        };
        const assistantMessage = {
            role: 'assistant',
            content: buildAssistantContent(nextPlan),
            plan: nextPlan,
            createdAt: now + 1
        };
        setSession(previous => normalizeStoredSession({
            messages: previous.messages.concat(userMessage, assistantMessage),
            lastPlan: nextPlan
        }));
    };

    const handleGenerate = async () => {
        const trimmedPrompt = prompt.trim();
        if (!trimmedPrompt || loading) return;
        setLoading(true);
        setError('');
        try {
            const shouldReuseMemory = isRevisionPrompt(trimmedPrompt);
            const previousPlan = shouldReuseMemory ? (plan || session.lastPlan) : null;
            const response = await fetch('/api/ai/scratch/generate', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                credentials: 'include',
                body: JSON.stringify({
                    prompt: trimmedPrompt,
                    mode: 'insert',
                    currentSprites: getCurrentSprites(vm),
                    locale: 'zh-CN',
                    previousPlan: previousPlan || undefined,
                    history: shouldReuseMemory ? compactHistoryForRequest(session.messages) : []
                })
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'AI 生成失败');
            }
            setPlan(data);
            appendConversation(trimmedPrompt, data);
            setPrompt('');
        } catch (err) {
            setError(err && err.message ? err.message : 'AI 生成失败');
        } finally {
            setLoading(false);
        }
    };

    const handleApply = async () => {
        if (!plan || applying) return;
        setApplying(true);
        setError('');
        try {
            await applyScratchAiPlan(vm, plan);
            setOpen(false);
        } catch (err) {
            setError(err && err.message ? err.message : '插入积木失败');
        } finally {
            setApplying(false);
        }
    };

    const handleClearSession = () => {
        const empty = createEmptySession();
        setSession(empty);
        setPlan(null);
        setPrompt('');
        setError('');
        saveSession(empty);
    };

    const clarificationQuestions = getClarificationQuestions(plan);
    const needsClarification = clarificationQuestions.length > 0;
    const recommendedReplies = needsClarification ? getRecommendedReplies(clarificationQuestions[0]) : [];
    const canApplyPlan = Boolean(plan && !needsClarification);
    const hasMemory = Boolean((session.messages && session.messages.length) || plan);
    const hasDraftPrompt = Boolean(prompt.trim());
    const draftIsRevision = hasDraftPrompt && isRevisionPrompt(prompt);

    const handlePickRecommendation = option => {
        const extra = prompt.trim();
        const nextPrompt = extra ?
            `${option.label}：${option.detail}\n补充说明：${extra}` :
            `${option.label}：${option.detail}`;
        setPrompt(nextPrompt);
    };

    const primaryButtonLabel = loading ? '生成中...' :
        applying ? '正在插入...' :
            hasDraftPrompt ? (needsClarification ? '发送确认并生成' : hasMemory && draftIsRevision ? '继续优化方案' : '生成新积木方案') :
                canApplyPlan ? '确认插入到作品' :
                    needsClarification ? '先完成需求确认' : '输入想法后生成';
    const primaryButtonIsApply = !hasDraftPrompt && canApplyPlan;
    const primaryButtonDisabled = loading || applying || (!hasDraftPrompt && !canApplyPlan);
    const handlePrimaryAction = () => {
        if (primaryButtonDisabled) return;
        if (hasDraftPrompt) {
            handleGenerate();
            return;
        }
        handleApply();
    };

    return (
        <div className={styles.root}>
            <button
                className={styles.floatingButton}
                title="AI 编程"
                type="button"
                onClick={() => setOpen(!open)}
            >
                <span className={styles.spark}>AI</span>
                <span>编程</span>
            </button>
            {open ? (
                <section
                    className={styles.panel}
                    ref={panelRef}
                    style={{left: `${panelPosition.x}px`, top: `${panelPosition.y}px`}}
                >
                    <header
                        className={styles.header}
                        onPointerDown={handleHeaderPointerDown}
                        onPointerMove={handleHeaderPointerMove}
                        onPointerUp={handleHeaderPointerUp}
                        onPointerCancel={handleHeaderPointerUp}
                    >
                        <div>
                            <h3>AI 编程</h3>
                            <p>先聊清楚目标，再把方案变成可插入的 Scratch 积木。</p>
                        </div>
                        <div className={styles.headerActions}>
                            {hasMemory ? (
                                <button
                                    className={styles.clearButton}
                                    type="button"
                                    onClick={handleClearSession}
                                >
                                    清空记忆
                                </button>
                            ) : null}
                            <button
                                className={styles.closeButton}
                                type="button"
                                onClick={() => setOpen(false)}
                            >
                                关闭
                            </button>
                        </div>
                    </header>
                    <div className={styles.body}>
                        <div className={styles.inputColumn}>
                            <div className={styles.note}>
                                生成会保存到本地浏览器缓存中；只有明确说“继续、修改、优化上一版”时才会沿用旧方案。
                            </div>
                            {needsClarification ? (
                                <div className={styles.clarificationBox}>
                                    <strong>AI 需要先确认这些信息</strong>
                                    <ul>
                                        {clarificationQuestions.map((question, index) => (
                                            <li key={`${question}-${index}`}>{question}</li>
                                        ))}
                                    </ul>
                                    <div className={styles.recommendations}>
                                        {recommendedReplies.map(option => (
                                            <button
                                                key={option.label}
                                                type="button"
                                                onClick={() => handlePickRecommendation(option)}
                                            >
                                                <strong>{option.label}</strong>
                                                <span>{option.detail}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                            <textarea
                                className={styles.prompt}
                                maxLength={1000}
                                placeholder={needsClarification ? '也可以直接补充你的想法，例如：我要射击游戏，敌人多一点。' : hasMemory ? '例如：写一个新的坦克大战；要改上一版时请说“继续优化上一版……”' : '例如：帮我写一个坦克大战小游戏'}
                                value={prompt}
                                onChange={event => setPrompt(event.target.value)}
                            />
                            {error ? <div className={styles.error}>{error}</div> : null}
                        </div>
                        <div className={styles.resultColumn}>
                            <div className={styles.processPanel}>
                                <div className={styles.processHeader}>
                                    <strong>生成过程</strong>
                                    <div className={styles.processBadges}>
                                        {plan && plan.superpowerUsed ? <span>Superpower 已使用</span> : null}
                                        <span>{loading ? '等待模型' : plan ? '已完成' : '等待开始'}</span>
                                    </div>
                                </div>
                                <ProcessTranscript loading={loading} plan={plan} />
                                {plan && !needsClarification ? (
                                    <div className={styles.generatedBlocks}>
                                        <strong>最终积木代码</strong>
                                        <p>{plan.planTitle}：{plan.summary}</p>
                                        <div className={styles.sprites}>
                                            {(plan.sprites || []).map(sprite => (
                                                <div className={styles.spriteCard} key={`generated-${sprite.displayName || sprite.spriteName}`}>
                                                    <strong>{sprite.displayName || sprite.spriteName}</strong>
                                                    <span>{sprite.spriteName}</span>
                                                    {(sprite.scripts || []).map((script, scriptIndex) => (
                                                        <div
                                                            className={styles.scriptBlock}
                                                            key={`generated-${sprite.spriteName}-${scriptIndex}`}
                                                        >
                                                            <b>{getEventLabel(script.event)}</b>
                                                            <FullActionList actions={script.actions || []} />
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : null}
                                <div className={styles.processLog}>
                                    {loading ? (
                                        <p>正在请求 AI 生成公开过程和可插入积木方案。</p>
                                    ) : plan ? (
                                        <p>{plan.fallback ? `已启用备用方案：${plan.fallbackReason || '模型未及时返回'}` : '上方内容来自本次 AI 返回的公开生成字段。'}</p>
                                    ) : (
                                        <p>输入作品目标后，这里会显示 AI 返回的公开生成内容。</p>
                                    )}
                                </div>
                            </div>
                            {plan ? (
                                <div className={styles.preview}>
                                    <div className={styles.previewBody}>
                                        <h4>{plan.planTitle}</h4>
                                        <p>{plan.summary}</p>
                                        {plan.taskBreakdown ? (
                                            <div className={styles.breakdown}>
                                                <strong>任务拆解</strong>
                                                <p>{plan.taskBreakdown.overview}</p>
                                                <ol>
                                                    {(plan.taskBreakdown.steps || []).map((step, index) => (
                                                        <li
                                                            className={step.status === 'current' ? styles.currentStep : ''}
                                                            key={`${step.title}-${index}`}
                                                        >
                                                            <b>{step.title}</b>
                                                            <span>{step.goal}</span>
                                                            {step.acceptance ? <em>{step.acceptance}</em> : null}
                                                        </li>
                                                    ))}
                                                </ol>
                                            </div>
                                        ) : null}
                                        {needsClarification ? null : (
                                            <div className={styles.sprites}>
                                                {(plan.sprites || []).map(sprite => (
                                                    <div className={styles.spriteCard} key={sprite.displayName || sprite.spriteName}>
                                                        <strong>{sprite.displayName || sprite.spriteName}</strong>
                                                        <span>{sprite.spriteName}</span>
                                                        <ul>
                                                            {(sprite.scripts || []).slice(0, 2).flatMap((script, scriptIndex) =>
                                                                (script.actions || []).slice(0, 4).map((action, actionIndex) => (
                                                                    <ActionSummary
                                                                        action={action}
                                                                        key={`${scriptIndex}-${actionIndex}-${action.type}`}
                                                                    />
                                                                ))
                                                            )}
                                                        </ul>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.placeholder}>
                                    <strong>生成结果会显示在这里</strong>
                                    <p>先描述作品目标，AI 会整理角色、脚本和可插入的积木方案。</p>
                                </div>
                            )}
                        </div>
                    </div>
                    <footer className={styles.footer}>
                        <span className={styles.statusText}>
                            {loading ? 'AI 正在生成，请稍候。' :
                                applying ? '正在把积木插入当前作品。' :
                                    needsClarification ? '请先补充需求，再生成积木方案。' :
                                        plan ? '确认后会把方案插入到当前 Scratch 作品。' :
                                            'AI 生成失败时会显示错误，不会插入本地备用积木。'}
                        </span>
                        <button
                            className={primaryButtonIsApply ? styles.applyButton : styles.generateButton}
                            disabled={primaryButtonDisabled}
                            type="button"
                            onClick={handlePrimaryAction}
                        >
                            {primaryButtonLabel}
                        </button>
                    </footer>
                </section>
            ) : null}
        </div>
    );
};

MaipaiAiProgramming.propTypes = {
    vm: PropTypes.shape({
        runtime: PropTypes.shape({
            targets: PropTypes.arrayOf(PropTypes.shape({
                isStage: PropTypes.bool,
                getName: PropTypes.func
            }))
        }).isRequired
    }).isRequired
};

export default MaipaiAiProgramming;
